import { useMemo } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import {
  useButtonPressedCallback,
  useLinkPressedCallback
} from '@zenbusiness/application-commons-clickstream';

import { BANK_OF_AMERICA_OFFER_URL } from '#client/constants/url';
import useModalContext from '#client/hooks/useModalContext';
import { CHASE_BANK_URL } from '#client/pages/BussinessBankingAccounts/hooks/useBankingCardOffers';

import BoAModal from '../components/BoAModal';

const BLUEVINE_URL =
  'https://www.bluevine.com/partner/zenbusiness-checking?utm_source=zenbusiness-0291763&utm_medium=partner_referral&utm_campaign=site&subid_1=xxx&subid_2=xxx&subid_3=xxx';

const useBankingCards = (clickstreamInfo) => {
  const { openModal } = useModalContext();
  const { businessEntityUuid } = useBusinessEntity();
  const bluevineButtonPressed = useLinkPressedCallback({
    elementName: 'apply_for_bluevine_nonqual_banking',
    elementText: 'Apply now',
    appPageName: clickstreamInfo.modalName
  });
  const bankOfAmericaButtonPressed = useButtonPressedCallback({
    elementName: 'view_BoA_details_nonqual_banking',
    elementText: 'View details',
    appPageName: clickstreamInfo.modalName
  });
  const chaseButtonPressed = useLinkPressedCallback({
    elementName: 'apply_for_chase_nonqual_banking',
    elementText: 'Apply now',
    appPageName: clickstreamInfo.modalName
  });

  const bankOfAmericaCard = useMemo(
    () => ({
      id: 'bankOfAmericaCard',
      logo: {
        src: '/logo/Bank-of-America-Logo.svg',
        alt: 'Bank of America Logo',
        width: 224
      },
      title: 'Business Advantage Banking',
      description: 'Earn bonuses totaling $300 to $800 with qualifying activities.',
      features: [
        '$300 checking bonus offer*',
        '$300 credit card statement credit offer*',
        'Separate your business and personal accounts',
        'Track business expenses more easily'
      ],
      disclaimer: {
        anchorText: '*See offer page for details',
        hyperlink: BANK_OF_AMERICA_OFFER_URL
      },
      disclosure: 'Deposit products are provided by Bank of America, N.A. Member FDIC.',
      ctaButton: {
        text: 'View details',
        onClick: () => {
          bankOfAmericaButtonPressed();
          openModal({ name: 'BoA Modal', component: BoAModal, isOpen: true });
        }
      }
    }),
    [bankOfAmericaButtonPressed, openModal]
  );

  const bluevineCard = useMemo(
    () => ({
      id: 'bluevineCard',
      logo: {
        src: '/s2/vector/bluevine-blue-logo.svg',
        alt: 'Bluevine Logo',
        width: 132
      },
      title: 'Business Checking Account',
      description:
        '6 months free of Bluevine Plus and the ability to earn a $300 signup bonus with qualifying activities.',
      features: [
        '$300 signup bonus when meeting qualifying activities',
        'Earn up to 2.7% APY on balances up to $250,000',
        'Unlimited transactions, no overdraft fees, and no fees on ACH payments',
        'Add up to 10 sub accounts each with designated account numbers',
        'Get up to $3 million in FDIC insurance'
      ],
      ctaButton: {
        text: 'Apply now',
        icon: '/s2/icon/external-link-fill.svg',
        onClick: () => {
          bluevineButtonPressed();
          window.open(BLUEVINE_URL, '_blank');
        }
      }
    }),
    [bluevineButtonPressed]
  );

  const chaseCard = useMemo(
    () => ({
      id: 'chaseCard',
      logo: {
        src: '/s2/vector/chase-for-business.svg',
        alt: 'Chase Logo',
        width: 132
      },
      title: 'Business Checking Account',
      description: 'Open an account and earn up to a $500 bonus with qualifying activities.',
      features: [
        'Multiple ways to pay and get paid, like built-in invoicing and card acceptance, Zelle®, and Tap to Pay on iPhone, to help improve your cash flow',
        'Access business insights and tools to help you grow',
        'Personal attention with a large scale presence — more than 4,900 branches and 15,400 ATMs',
        'Multiple ways to waive the $15 monthly service fee'
      ],
      ctaButton: {
        text: 'Apply now',
        icon: '/s2/icon/external-link-fill.svg',
        onClick: () => {
          chaseButtonPressed();
          window.open(`${CHASE_BANK_URL}&u1=${businessEntityUuid}`, '_blank');
        }
      }
    }),
    [chaseButtonPressed, businessEntityUuid]
  );

  return {
    bankOfAmericaCard,
    bluevineCard,
    chaseCard
  };
};

export default useBankingCards;
